import * as React from "react";
import {useTranslation} from "@unibuddy/intl";
import styled from "styled-components";
import noop from "lodash/noop";
import {Box, Button, Select, TextInput, View} from "@unibuddy/patron";

import {INPUT_HEIGHT} from "../../widgets/modules/Settings/components/StyleConstants";
import {makeCreateAsYouType, parseFullPhoneNumber, parsePhoneNumber} from "./parsePhoneNumber";
import {PhoneNumber} from "./PhoneNumber";
import {MEDIUM_SCREEN_WIDTH} from "../../widgets/modules/NextBestActions/NextBestActions";
import {areaCodes, getDialCode} from "./dialCodes";
import {IntlNamespaces} from "../../translations/types";
import {
    INTL_EDIT,
    INTL_EDIT_MOBILE,
    INTL_SAVE,
    INTL_SAVE_MOBILE,
} from "../../translations/settings";

type PhoneInputProps = {
    inputLabel: string;
    inputValue: string;
    inputName: string;
    flexDirection?: "row" | "column";
    showEditable?: boolean;
    disabled?: boolean;
    placeHolder?: string;
    onFinishEdit?: () => void | Promise<void>;
    onChange?: (phoneNumber: string) => void;
};

const AreaCodeSelect = styled(Select)`
    height: ${INPUT_HEIGHT}px;
    width: 100%;
    @media (min-width: ${MEDIUM_SCREEN_WIDTH}) {
        width: 275px;
    }
`;

const PhoneTextInput = styled(TextInput)`
    width: 100%;
`;

const PhoneTextInputWrapper = styled(Box)`
    width: 100%;
    padding-top: 12px;
    padding-left: 0px;
    @media (min-width: ${MEDIUM_SCREEN_WIDTH}) {
        padding-top: 0px;
        padding-left: 12px;
    }
`;

const InputWrapper = styled(Box)`
    display: flex;
    width: 100%;
    flex-direction: column;
    padding-top: 12px;
    @media (min-width: ${MEDIUM_SCREEN_WIDTH}) {
        flex-direction: ${(props) => props.flexDirection ?? "row"};
        padding-top: ${(props) => (props.flexDirection === "column" ? "12px" : "0px")};
    }
`;

const ButtonWrapper = styled(Box)`
    padding-top: 12px;
    padding-left: 0px;
    width: 100%;
    @media (min-width: ${MEDIUM_SCREEN_WIDTH}) {
        min-width: 83px;
        width: 83px;
        padding-top: 0px;
        padding-left: 12px;
    }
`;

export const PhoneInput: React.FC<PhoneInputProps> = React.memo((props) => {
    const {t} = useTranslation(IntlNamespaces.SETTINGS);
    const [isEditing, setIsEditing] = React.useState(false);
    const [phoneNumber, setPhoneNumber] = React.useState<PhoneNumber>(
        parseFullPhoneNumber(props.inputValue),
    );
    const areaCodeSelectId = `${props.inputName}-area-code`;
    const phoneRegionCode = phoneNumber.getRegionCode();

    const handleToggleEdit = React.useCallback(() => {
        const callback = async () => {
            if (isEditing) {
                await props?.onFinishEdit();
            } else {
                /* Patron's select element does not work with React.useRef() and we want to focus on it
                 * for accessibility reasons */
                const element = document.getElementById(areaCodeSelectId);
                setTimeout(() => element?.focus?.());
            }
            setIsEditing(!isEditing);
        };
        callback();
    }, [isEditing, props, areaCodeSelectId]);

    const propsOnChange = props.onChange ?? noop;

    const phoneNumberAsYouType = React.useMemo(
        () => makeCreateAsYouType(phoneRegionCode),
        [phoneRegionCode],
    );

    const handleChangePhoneNumber = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const newPhoneNumber = phoneNumberAsYouType(value);
            setPhoneNumber(newPhoneNumber);
            /* Set whatever is controlling this component with a full unformatted phone number */
            propsOnChange(newPhoneNumber.getFullNumber());
        },
        [phoneNumberAsYouType, propsOnChange],
    );

    const handleChangeRegionCode = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value;
            const areaCode = getDialCode(value);
            const newPhoneNumber = parsePhoneNumber(
                areaCode[3],
                phoneNumber.getSignificant(),
                value,
            );
            setPhoneNumber(newPhoneNumber);
            /* Set whatever is controlling this component with a full unformatted phone number */
            propsOnChange(newPhoneNumber.getFullNumber());
        },
        [phoneNumber, propsOnChange],
    );

    const intlAriaLabel = isEditing ? INTL_SAVE_MOBILE : INTL_EDIT_MOBILE;
    const intlButtonText = isEditing ? INTL_SAVE : INTL_EDIT;
    const ariaLabel = t(intlAriaLabel.key, intlAriaLabel.defaultValue);
    const btnText = t(intlButtonText.key, intlButtonText.defaultValue);

    return (
        <View alignItems="flex-start" w="100%">
            <label htmlFor={props.inputName} style={{fontWeight: "700"}}>
                {props.inputLabel}
            </label>
            <InputWrapper flexDirection={props.flexDirection}>
                <AreaCodeSelect
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                    formatValue={(e) => e}
                    aria-label="mobile area code"
                    autoFocus
                    onChange={handleChangeRegionCode}
                    value={phoneNumber.getRegionCode()}
                    id={areaCodeSelectId}
                    data-test-id={`dropdown-${props.inputName}`}
                    disabled={props.showEditable && !isEditing}
                >
                    {areaCodes.map(([regionCode, flag, country, dialCode]) => {
                        return (
                            <option value={regionCode} key={`${dialCode}-${country}`}>
                                {flag} {dialCode} {country}
                            </option>
                        );
                    })}
                </AreaCodeSelect>
                <PhoneTextInputWrapper>
                    <PhoneTextInput
                        aria-label="mobile number"
                        id={props.inputName}
                        disabled={props.showEditable && !isEditing}
                        onChange={handleChangePhoneNumber}
                        data-test-id={`input-${props.inputName}`}
                        value={phoneNumber.getDisplayNumber()}
                        placeholder={props.placeHolder}
                        style={{height: `${INPUT_HEIGHT}px`}}
                    />
                </PhoneTextInputWrapper>
                {props.showEditable && (
                    <ButtonWrapper>
                        <Button
                            aria-label={ariaLabel}
                            disabled={props.disabled}
                            data-test-id={`edit-${props.inputName}`}
                            style={{height: `${INPUT_HEIGHT}px`, width: "100%"}}
                            onClick={handleToggleEdit}
                        >
                            {btnText}
                        </Button>
                    </ButtonWrapper>
                )}
            </InputWrapper>
        </View>
    );
});

PhoneInput.displayName = "PhoneInput";
